import { format } from 'date-fns'
import type { PaperPosition } from '../../types'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../../config/glossary'
import { formatPrice } from '../../utils/marketPrice'
import { formatPnlPct } from '../../utils/pnl'
import TerminalPanel from '../dashboard/TerminalPanel'
import EquityCurveChart from '../dashboard/EquityCurveChart'
import { PAPER_CLOSED_LIMIT, PaperTrade, paperAvgRBasis, usePaperRecord } from './paperRecord'
import {
  AvgRTile,
  ClosedColumn,
  ClosedTradesPanel,
  DrawdownReadout,
  EmptyBox,
  ErrorState,
  LoadingState,
  OutcomeBadge,
  StatTile,
  WinRateTile,
  formatR,
  pnlClass,
} from './recordUI'
import { displaySymbol } from '../../utils/displaySymbol'

const EXIT_REASONS: Record<NonNullable<PaperPosition['exit_reason']>, string> = {
  trailing_stop: 'trailing stop',
  regime_off: 'BTC filter off',
  stale_data: 'stale data',
  kill_switch: 'kill rule',
}

const money = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })

const closedOn = (trade: PaperTrade) => (trade.closedAt != null ? format(trade.closedAt, 'MMM d, yyyy') : '—')

const exitReason = (trade: PaperTrade) =>
  trade.position.exit_reason ? EXIT_REASONS[trade.position.exit_reason] ?? trade.position.exit_reason : '—'

const tradeLabel = (trade: PaperTrade) => (
  <>
    <span className="font-semibold text-gray-100">{displaySymbol(trade.position.symbol)}</span>{' '}
    <span className="text-xs text-gray-400">{trade.position.window}d breakout</span>
  </>
)

const COLUMNS: ClosedColumn<PaperTrade>[] = [
  { header: '#', render: (t) => t.n, cellClass: () => 'font-mono text-gray-500' },
  { header: 'Closed', render: closedOn, cellClass: () => 'font-mono tabular-nums text-gray-400' },
  { header: 'Trade', render: tradeLabel },
  { header: 'Outcome', render: (t) => <OutcomeBadge win={t.win} /> },
  {
    header: 'Entry → exit',
    align: 'right',
    render: (t) =>
      `$${formatPrice(t.position.entry_price)} → ${t.position.exit_price != null ? `$${formatPrice(t.position.exit_price)}` : '—'}`,
    cellClass: () => 'text-gray-300',
  },
  { header: 'Exit', render: exitReason, cellClass: () => 'text-gray-400' },
  { header: 'Net of fees', align: 'right', render: (t) => formatPnlPct(t.netPct), cellClass: (t) => pnlClass(t.netPct) },
  { header: 'R', align: 'right', render: (t) => (t.r != null ? formatR(t.r) : '—'), cellClass: () => 'text-gray-300' },
]

/**
 * The breakout forward test's automated record: pretend money on real prices,
 * every trade recorded by the daily run itself, net of fees. Until a trade has
 * closed it shows the test in progress; it never falls back to the old
 * engine's numbers.
 */
const PaperRecordView = () => {
  const { record, failed, reload } = usePaperRecord()

  if (failed && !record) return <ErrorState message="The paper record is unavailable right now." onRetry={reload} />
  if (!record) return <LoadingState label="Loading the paper record…" />

  const { account, closed, wins, losses, winRate, avgWinPct, avgLossPct, avgR, rCount, curve, maxDrawdownPct, trades } =
    record
  const notStarted = account.status === 'not_started'
  const inProgress = closed === 0
  const progress = notStarted
    ? 'The forward test has not started yet.'
    : `Day ${account.day} of ~${account.eval_days} · ${account.open_positions} open position${account.open_positions === 1 ? '' : 's'}`

  return (
    <>
      {inProgress && (
        <div
          className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-4 py-3 text-sm"
          data-testid="paper-in-progress"
        >
          <p className="font-semibold text-violet-100">Paper · live forward test (in progress)</p>
          <p className="mt-0.5 text-xs text-gray-400">
            {progress} No trades have closed yet, so there is no record to show. It starts with the first close, win
            or loss. The signal engine's numbers are never shown in its place.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <WinRateTile winRate={winRate} closed={closed} wins={wins} losses={losses} noun="closed trades" />

        <StatTile
          label="Avg win"
          hint={GLOSSARY.paperAvgWin}
          testId="stat-avg-win"
          value={avgWinPct == null ? '—' : formatPnlPct(avgWinPct)}
          valueClass={avgWinPct == null ? 'text-gray-500' : 'text-green-400'}
        >
          net of fees, per winning trade · {wins} win{wins === 1 ? '' : 's'}
        </StatTile>

        <StatTile
          label="Avg loss"
          hint={GLOSSARY.paperAvgLoss}
          testId="stat-avg-loss"
          value={avgLossPct == null ? '—' : formatPnlPct(avgLossPct)}
          valueClass={avgLossPct == null ? 'text-gray-500' : 'text-red-400'}
        >
          net of fees, per losing trade · {losses} loss{losses === 1 ? '' : 'es'}
        </StatTile>

        <AvgRTile
          avgR={avgR}
          basis={
            closed > 0 && rCount === 0
              ? 'not reported by this API version'
              : paperAvgRBasis(account)
          }
        />

        <StatTile
          label="Account return"
          hint={GLOSSARY.paperReturn}
          testId="stat-net"
          value={inProgress ? '—' : formatPnlPct(account.profit_loss_pct)}
          valueClass={inProgress ? 'text-gray-500' : pnlClass(account.profit_loss_pct)}
        >
          {inProgress ? (
            'no closed trades yet'
          ) : (
            <>
              <span className="font-mono tabular-nums">
                net of fees · {money(account.current_balance)} of {money(account.starting_balance)}
              </span>
              <span className="mt-1 block" data-testid="paper-balance-note">
                Balance includes open positions at the latest daily close; win rate and average R count closed trades
                only.
              </span>
            </>
          )}
        </StatTile>
      </div>

      <TerminalPanel
        title="Equity curve · account value, net of fees"
        tone="violet"
        subtitle={
          <p className="text-xs text-gray-500">
            The pretend account valued at every daily close, open positions included: not smoothed.
          </p>
        }
        action={!inProgress ? <DrawdownReadout value={maxDrawdownPct} unit="peak" /> : undefined}
      >
        <div className="h-64 md:h-80">
          {inProgress ? (
            <EmptyBox title="Forward test in progress" detail={`${progress} The curve starts with the first closed trade.`} />
          ) : curve.length < 2 ? (
            <EmptyBox title="Not enough history to plot yet" detail="The account is valued once per closed daily candle." />
          ) : (
            <EquityCurveChart
              points={curve}
              mode="days"
              ariaLabel={`Paper account equity over ${account.day} days: ${formatPnlPct(account.profit_loss_pct)} net of fees, ${wins} wins, ${losses} losses, max drawdown ${maxDrawdownPct.toFixed(2)}% from peak`}
            />
          )}
        </div>
      </TerminalPanel>

      <ClosedTradesPanel
        title="Closed trades"
        note="Each breakout can open up to 4 positions, one per window (20/30/40/55 days); each counts as a trade, as the kill rule counts them."
        total={closed}
        rows={trades}
        rowKey={(t) => t.position.id}
        columns={COLUMNS}
        win={(t) => t.win}
        empty={<EmptyBox title="No closed trades yet" detail={progress} />}
        mobileTitle={(t) => (
          <>
            <span className="font-mono text-gray-500">#{t.n}</span> {tradeLabel(t)}
          </>
        )}
        mobileDetail={(t) => (
          <>
            <span>
              {closedOn(t)} · {exitReason(t)}
            </span>
            <span>
              <span className={pnlClass(t.netPct)}>{formatPnlPct(t.netPct)}</span> net
              {t.r != null && ` · ${formatR(t.r)}`}
            </span>
          </>
        )}
      />
      {trades.length < closed && (
        <p className="text-xs text-gray-500">
          The list holds the newest {Math.min(trades.length, PAPER_CLOSED_LIMIT)} of {closed} closed trades; the stats
          above count all {closed}.
        </p>
      )}
    </>
  )
}

export default PaperRecordView
